<script lang="ts">
  import { uniq, without } from 'lodash-es'
  import { onChange } from '#app/lib/svelte/svelte'
  import InfoTip from '#components/info_tip.svelte'
  import { getGroupVisibilityKey, isNotGroupVisibilityKey, commonVisibilityKeys } from '#general/lib/visibility'
  import { userGroupsStore } from '#groups/lib/groups_data'
  import { guessInitialVisibility } from '#inventory/components/lib/item_creation_helpers'
  import type { VisibilityKey } from '#server/types/visibility'
  import { i18n, I18n } from '#user/lib/i18n'

  let {
    visibility = $bindable(),
    maxHeight = '25em',
    showDescription = false,
    showTip = false,
  }: {
    visibility: VisibilityKey[]
    maxHeight?: string
    showDescription?: boolean
    showTip?: boolean
  } = $props()

  visibility = guessInitialVisibility(visibility)

  // Group keys will be added to 'checked' once $userGroupsStore will have been populated
  let checked: VisibilityKey[] = $state(visibility[0] === 'public' ? commonVisibilityKeys : visibility)

  function initCheckedGroupKeys () {
    if (visibility.includes('public') || visibility.includes('groups')) checkAllGroups()
  }

  function updateCheckedGroupsKeys (allGroupsChecked) {
    if (allGroupsChecked) checkAllGroups()
    else uncheckAllGroups()
  }

  function checkAllGroups () {
    checked = uniq([ ...checked, 'groups', ...allGroupsVisibilityKeys ]) as VisibilityKey[]
  }

  function uncheckAllGroups () {
    checked = without(checked, 'public', 'groups', ...allGroupsVisibilityKeys) as VisibilityKey[]
  }

  function onPublicClick (e) {
    if (e.target.checked) {
      checked = [ ...commonVisibilityKeys, ...allGroupsVisibilityKeys ] as VisibilityKey[]
    }
  }

  function onFriendsClick (e) {
    if (!e.target.checked) {
      checked = without(checked, 'public', 'friends')
    }
  }

  function onGroupsClick (e) {
    updateCheckedGroupsKeys(e.target.checked)
  }

  function onSingleGroupClick (e) {
    if (!e.target.checked) {
      checked = without(checked, 'public', 'groups', e.target.value)
    }
  }

  function updateVisibility () {
    if (checked.includes('public')) {
      visibility = [ 'public' ]
    } else if (checked.includes('groups')) {
      visibility = checked.filter(isNotGroupVisibilityKey)
    } else {
      visibility = checked
    }
  }
  // Needs to be above reactive call to initCheckedGroupKeys
  const allGroupsVisibilityKeys = $derived($userGroupsStore.map(getGroupVisibilityKey))
  $effect(() => {
    onChange($userGroupsStore, initCheckedGroupKeys)
  })
  $effect(() => {
    onChange(checked, updateVisibility)
  })
</script>

<fieldset>
  <legend>
    <p class="title">{I18n('visibility')}</p>
    {#if showDescription}
      <p class="description">{i18n('Who should be allowed to see it?')}</p>
    {/if}
  </legend>

  <div
    class="options"
    style:max-height={maxHeight}
  >
    <label>
      <input
        type="checkbox"
        value="public"
        onclick={onPublicClick}
        bind:group={checked}
      />
      {I18n('public')}
    </label>

    <label class:inferred={visibility.includes('public')}
    >
      <input
        type="checkbox"
        value="friends"
        onclick={onFriendsClick}
        bind:group={checked}
      />
      {I18n('friends')}
    </label>

    <label
      class="has-infotip"
      class:inferred={visibility.includes('public')}
    >
      <input
        type="checkbox"
        value="groups"
        onclick={onGroupsClick}
        bind:group={checked}
      />
      <span class="title">{I18n('groups')}</span>
      {#if showTip}
        <InfoTip text={i18n('Include all your present and future groups')} />
      {/if}
    </label>

    {#each $userGroupsStore as group (group._id)}
      <label
        class="indent"
        class:inferred={visibility.includes('public') || visibility.includes('groups')}
      >
        <input
          type="checkbox"
          value="group:{group._id}"
          onclick={onSingleGroupClick}
          bind:group={checked}
        />
        {group.name}
      </label>
    {/each}
  </div>
</fieldset>

<style lang="scss">
  @import "general/scss/utils";
  .options{
    overflow-y: auto;
  }
  legend{
    padding: 0.2em 0;
  }
  label{
    padding: 0.2em 0.5em;
    font-size: 1rem;
    color: $dark-grey;
    white-space: pre-wrap;
    &:hover{
      background-color: $light-grey;
    }
  }
  .indent{
    margin-inline-start: 1.3em;
  }
  .description{
    font-size: 0.9rem;
    color: $label-grey;
  }
  .inferred:not(:hover){
    opacity: 0.7;
  }
</style>
